import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import SyncAltIcon from 'components/material-symbol/icons/SyncAlt'
import AddOutlinedIcon from 'components/material-symbol/icons/Add'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import useIsMobile from 'lib/hooks/use-is-mobile'
import { OUTLINED_INPUT_FOCUS_BORDER } from 'lib/form-field-styles'
import useCalendarStyles from 'pages/pga-coach/bookings/schedule/calendar/calendar-styles'
import {
  availabilityFill,
  isGoogleSyncedBlock,
  SYNCED_BLOCK_TOOLTIP,
} from './availability-colors'
import {
  HOUR_HEIGHT,
  HOUR_HEIGHT_MOBILE,
  dayLabels,
  formatHourLabel,
  buildRecurringByDayIndex,
  buildOverridesByDateKey,
  buildBlockedByDay,
  computeVisibleHourRange,
} from './utils'

const HOUR_LABEL_WIDTH = 60
const HOUR_LABEL_WIDTH_MOBILE = 44

const useStyles = makeStyles(theme => ({
  hourCellInteractive: {
    cursor: 'pointer',
    outline: 'none',
    '&:hover $addSlotTarget': {
      backgroundColor: '#E8EFFF',
      borderColor: OUTLINED_INPUT_FOCUS_BORDER,
    },
    '&:hover $plusIcon': {
      opacity: 1,
    },
    '&:focus-visible $addSlotTarget': {
      backgroundColor: '#E8EFFF',
      borderColor: OUTLINED_INPUT_FOCUS_BORDER,
    },
    '&:focus-visible $plusIcon': {
      opacity: 1,
    },
  },
  addSlotTarget: {
    position: 'absolute',
    top: 4,
    left: 4,
    right: 4,
    bottom: 4,
    borderRadius: 10,
    border: '1px dashed transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    pointerEvents: 'none',
  },
  plusIcon: {
    fontSize: 20,
    opacity: 0,
    color: OUTLINED_INPUT_FOCUS_BORDER,
    pointerEvents: 'none',
    transition: 'opacity 120ms ease',
    [theme.breakpoints.only('xs')]: {
      fontSize: 16,
    },
  },
  block: {
    position: 'absolute',
    left: 4,
    right: 4,
    borderRadius: 12,
    padding: '6px 8px',
    overflow: 'hidden',
    cursor: 'default',
    border: 'none',
    boxSizing: 'border-box',
    zIndex: 1,
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.35,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    boxShadow: 'none',
    [theme.breakpoints.only('xs')]: {
      left: 2,
      right: 2,
      padding: '4px 4px',
      borderRadius: 10,
      fontSize: '0.6875rem',
    },
  },
  recurring: availabilityFill('recurring'),
  override: availabilityFill('override'),
  blocked: {
    ...availabilityFill('blocked'),
    zIndex: 2,
  },
  syncedBlocked: {
    ...availabilityFill('synced'),
    cursor: 'default',
    zIndex: 1,
  },
  syncedIcon: {
    fontSize: 14,
    flexShrink: 0,
    marginTop: 1,
    color: '#410002',
  },
  blockLabel: {
    minWidth: 0,
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: 4,
    overflow: 'hidden',
    lineHeight: 1.25,
  },
}))

const formatBlockTimeFractional = h => {
  const hour24 = Math.floor(h)
  const minute = Math.round((h - hour24) * 60)
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return minute === 0
    ? `${hour12}\u00A0${period}`
    : `${hour12}:${String(minute).padStart(2, '0')}\u00A0${period}`
}

const Block = ({
  start,
  end,
  variant,
  label,
  synced,
  hourHeight,
  classes,
  gridStartHour,
  gridEndHour,
}) => {
  const clampedStart = Math.max(start, gridStartHour)
  const clampedEnd = Math.min(end, gridEndHour)
  if (clampedEnd <= clampedStart) return null
  const top = (clampedStart - gridStartHour) * hourHeight
  const height = (clampedEnd - clampedStart) * hourHeight
  const inner = (
    <div
      className={`${classes.block} ${classes[variant]}`}
      style={{ top, height }}
      onClick={e => e.stopPropagation()}
      aria-label={synced ? SYNCED_BLOCK_TOOLTIP : undefined}
    >
      {synced && <SyncAltIcon className={classes.syncedIcon} />}
      <span className={classes.blockLabel}>
        {label || (
          <>
            <span>{formatBlockTimeFractional(start)}</span>
            <span>
              {'\u2013\u00A0'}
              {formatBlockTimeFractional(end)}
            </span>
          </>
        )}
      </span>
    </div>
  )

  if (!synced) return inner

  return (
    <Tooltip title={SYNCED_BLOCK_TOOLTIP} placement="top">
      {inner}
    </Tooltip>
  )
}

const AvailabilityCalendarGrid = ({
  weekRange,
  recurringTimeSlots,
  overrides,
  blocks,
  timeZone,
  onCellClick,
}) => {
  const classes = useStyles()
  const calendarClasses = useCalendarStyles()
  const isMobile = useIsMobile()
  const hourHeight = isMobile ? HOUR_HEIGHT_MOBILE : HOUR_HEIGHT
  const labelWidth = isMobile ? HOUR_LABEL_WIDTH_MOBILE : HOUR_LABEL_WIDTH
  const recurringByDay = buildRecurringByDayIndex(recurringTimeSlots)
  const overridesByDay = buildOverridesByDateKey(overrides, weekRange.start)
  const blockedByDay = buildBlockedByDay(blocks, weekRange.start, timeZone)
  const { startHour, endHour } = computeVisibleHourRange(
    recurringByDay,
    overridesByDay,
    blockedByDay,
  )
  const today = new Date().toISOString().slice(0, 10)
  const todayStart = DateTime.local().startOf('day')
  const hours = []
  for (let h = startHour; h < endHour; h++) hours.push(h)
  const gridHeight = hours.length * hourHeight

  const activateCell = (dayIdx, hour, anchorPosition) => {
    if (!onCellClick) return
    const dayDate = weekRange.start.plus({ days: dayIdx })
    if (dayDate.startOf('day') < todayStart) return
    const startTime = dayDate.set({
      hour,
      minute: 0,
      second: 0,
      millisecond: 0,
    })
    onCellClick({
      date: dayDate.startOf('day'),
      startTime,
      anchorPosition,
    })
  }

  const handleCellClick = (dayIdx, hour) => e => {
    activateCell(dayIdx, hour, { left: e.clientX, top: e.clientY })
  }

  const handleCellKeyDown = (dayIdx, hour) => e => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    activateCell(dayIdx, hour, {
      left: rect.left + rect.width / 2,
      top: rect.top + rect.height / 2,
    })
  }

  return (
    <>
      <Box className={calendarClasses.weekHeaderRow}>
        <Box
          className={calendarClasses.weekHeaderSpacer}
          style={{ width: labelWidth }}
        />
        {dayLabels.map((label, i) => {
          const dayDate = weekRange.start.plus({ days: i })
          const isToday = dayDate.toISODate() === today
          return (
            <Box key={label} className={calendarClasses.weekHeaderCell}>
              <Box className={calendarClasses.weekHeaderLabel}>
                <span className={calendarClasses.weekDayName}>{label}</span>
                <span
                  className={
                    isToday
                      ? calendarClasses.weekDayNumberToday
                      : calendarClasses.weekDayNumber
                  }
                >
                  {dayDate.day}
                </span>
              </Box>
            </Box>
          )
        })}
      </Box>

      <Box className={calendarClasses.timeGridBody}>
        <Box
          className={calendarClasses.hourLabelsColumn}
          style={{ width: labelWidth }}
        >
          {hours.map(h => (
            <Typography
              key={h}
              className={calendarClasses.hourLabel}
              style={{ height: hourHeight }}
            >
              {formatHourLabel(h)}
            </Typography>
          ))}
        </Box>

        <Box className={calendarClasses.dayColumns}>
          {dayLabels.map((label, dayIdx) => {
            const blocksForDay = blockedByDay[dayIdx]
            const allDayBlocks = blocksForDay.filter(seg => seg.allDay)
            const timedBlocks = blocksForDay.filter(seg => !seg.allDay)
            const dayDate = weekRange.start.plus({ days: dayIdx })
            const isPastDay = dayDate.startOf('day') < todayStart
            return (
              <Box
                key={label}
                className={calendarClasses.dayColumn}
                style={{ height: gridHeight }}
              >
                {hours.map(h => {
                  const cellLabel = `Add time at ${formatHourLabel(
                    h,
                  )} on ${dayDate.toFormat('EEEE, MMMM d')}`
                  return (
                    <Box
                      key={h}
                      className={`${calendarClasses.hourCell} ${
                        isPastDay ? '' : classes.hourCellInteractive
                      }`}
                      style={{ height: hourHeight }}
                      onClick={
                        isPastDay ? undefined : handleCellClick(dayIdx, h)
                      }
                      onKeyDown={
                        isPastDay ? undefined : handleCellKeyDown(dayIdx, h)
                      }
                      role={isPastDay ? undefined : 'button'}
                      tabIndex={isPastDay ? undefined : 0}
                      aria-label={isPastDay ? undefined : cellLabel}
                    >
                      {!isPastDay && (
                        <Box className={classes.addSlotTarget}>
                          <AddOutlinedIcon className={classes.plusIcon} />
                        </Box>
                      )}
                    </Box>
                  )
                })}
                {allDayBlocks.map((seg, i) => {
                  const isSynced = isGoogleSyncedBlock(seg.block)
                  return (
                    <Block
                      key={`b-allday-${i}`}
                      start={startHour}
                      end={endHour}
                      variant={isSynced ? 'syncedBlocked' : 'blocked'}
                      synced={isSynced}
                      label="All day"
                      hourHeight={hourHeight}
                      classes={classes}
                      gridStartHour={startHour}
                      gridEndHour={endHour}
                    />
                  )
                })}
                {recurringByDay[dayIdx].map((seg, i) => (
                  <Block
                    key={`r-${i}`}
                    start={seg.start}
                    end={seg.end}
                    variant="recurring"
                    hourHeight={hourHeight}
                    classes={classes}
                    gridStartHour={startHour}
                    gridEndHour={endHour}
                  />
                ))}
                {overridesByDay[dayIdx].map((seg, i) => (
                  <Block
                    key={`o-${i}`}
                    start={seg.start}
                    end={seg.end}
                    variant="override"
                    hourHeight={hourHeight}
                    classes={classes}
                    gridStartHour={startHour}
                    gridEndHour={endHour}
                  />
                ))}
                {timedBlocks.map((seg, i) => {
                  const isSynced = isGoogleSyncedBlock(seg.block)
                  return (
                    <Block
                      key={`b-timed-${i}`}
                      start={seg.start}
                      end={seg.end}
                      variant={isSynced ? 'syncedBlocked' : 'blocked'}
                      synced={isSynced}
                      hourHeight={hourHeight}
                      classes={classes}
                      gridStartHour={startHour}
                      gridEndHour={endHour}
                    />
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Box>
    </>
  )
}

export default AvailabilityCalendarGrid
